

import { PWASettings } from "@/context/SettingsContext";
import ImageUploadField from "@/components/ImageUploadField";
import { getImageUrl } from "@/lib/utils";

interface PWASettingsSectionProps {
  data: PWASettings;
  logoUrl: string;
  faviconUrl: string;
  onChange: (data: PWASettings) => void;
  onSave: (data: PWASettings) => void;
  onGenerateLogos: () => void;
  onLogoChange?: (url: string) => void;
  onFaviconChange?: (url: string) => void;
  saving?: boolean;
  readOnly?: boolean;
}

export default function PWASettingsSection({
  data,
  logoUrl,
  faviconUrl,
  onChange,
  onSave,
  onGenerateLogos,
  onLogoChange,
  onFaviconChange,
  saving = false,
  readOnly = false,
}: PWASettingsSectionProps) {
  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-stone-800 font-serif">PWA / App Settings</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">Install Prompt</span>
          <button
            onClick={() => onChange({ ...data, installPromptEnabled: !data.installPromptEnabled })}
            disabled={readOnly}
            className={`w-10 h-5 rounded-full transition-colors relative ${
              data.installPromptEnabled ? "bg-emerald-500" : "bg-stone-300"
            } ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
              data.installPromptEnabled ? "left-5" : "left-0.5"
            }`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">App Name *</label>
          <input
            type="text"
            value={data.appName}
            onChange={(e) => onChange({ ...data, appName: e.target.value })}
            disabled={readOnly}
            placeholder="Shalimar ERP"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 text-stone-700 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
          <p className="text-xs text-stone-400 mt-1">Full name of your application (max 30 chars)</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">Short Name *</label>
          <input
            type="text"
            value={data.shortName}
            onChange={(e) => onChange({ ...data, shortName: e.target.value })}
            disabled={readOnly}
            placeholder="Shalimar"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 text-stone-700 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
          />
          <p className="text-xs text-stone-400 mt-1">Short version for home screen (max 12 chars)</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
          <textarea
            value={data.description}
            onChange={(e) => onChange({ ...data, description: e.target.value })}
            disabled={readOnly}
            placeholder="Premium Event Management ERP for banquet halls and event venues"
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-stone-200 text-stone-700 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50 resize-none"
          />
          <p className="text-xs text-stone-400 mt-1">Brief description of your app (max 150 chars)</p>
        </div>

        {/* Main Logo & Favicon */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Main Logo</label>
            <ImageUploadField
              label="Main Logo"
              value={logoUrl}
              onChange={(url) => onLogoChange?.(url)}
              type="logo"
              settingKey="theme.logoUrl"
              compact={true}
            />
            <p className="text-xs text-stone-400 mt-1">
              Upload your main logo. Recommended size: 512x512px, max 2MB.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Favicon</label>
            <ImageUploadField
              label="Favicon"
              value={faviconUrl}
              onChange={(url) => onFaviconChange?.(url)}
              type="favicon"
              settingKey="theme.faviconUrl"
              compact={true}
            />
            <p className="text-xs text-stone-400 mt-1">
              Upload favicon. Recommended size: 180x180px or 32x32px.
            </p>
          </div>
        </div>

        {/* Generated Icons */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">192x192 Icon</label>
          <div className="space-y-2">
            <ImageUploadField
              label="192x192 Icon"
              value={data.icon192}
              onChange={(url) => onChange({ ...data, icon192: url })}
              type="pwa"
              settingKey="pwa.icon192"
              compact={true}
            />
            <p className="text-xs text-stone-400">For Android devices and PWA icons</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">512x512 Icon</label>
          <div className="space-y-2">
            <ImageUploadField
              label="512x512 Icon"
              value={data.icon512}
              onChange={(url) => onChange({ ...data, icon512: url })}
              type="pwa"
              settingKey="pwa.icon512"
              compact={true}
            />
            <p className="text-xs text-stone-400">For app stores and high-resolution displays</p>
          </div>
        </div>

        {/* Generate Icons Button */}
        <div className="md:col-span-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-stone-800 mb-1">Auto-Generate Icons</h4>
                <p className="text-sm text-stone-600 mb-3">
                  Automatically generate all required icon sizes from your main logo. This ensures consistency and saves time.
                </p>
                <button
                  onClick={onGenerateLogos}
                  disabled={!logoUrl || saving || readOnly}
                  className="px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {saving ? "Generating..." : readOnly ? "View Only" : "Generate All Icons"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="md:col-span-2">
          <h3 className="text-sm font-semibold text-stone-800 mb-3">Preview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Mobile Preview */}
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs font-medium text-stone-500 mb-2">Mobile (192x192)</div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="w-16 h-16 mx-auto bg-stone-200 rounded-xl flex items-center justify-center">
                  {data.icon192 ? (
                    <img src={getImageUrl(data.icon192)} alt="App icon" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <svg className="w-8 h-8 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div className="text-center mt-2">
                  <div className="text-xs font-medium text-stone-700 truncate">
                    {data.shortName || "App Name"}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Preview */}
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs font-medium text-stone-500 mb-2">Desktop (512x512)</div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="w-20 h-20 mx-auto bg-stone-200 rounded-xl flex items-center justify-center">
                  {data.icon512 ? (
                    <img src={getImageUrl(data.icon512)} alt="App icon" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <svg className="w-10 h-10 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div className="text-center mt-2">
                  <div className="text-xs font-medium text-stone-700 truncate">
                    {data.appName || "Application Name"}
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {data.description || "App description"}
                  </div>
                </div>
              </div>
            </div>

            {/* Install Preview */}
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="text-xs font-medium text-stone-500 mb-2">Install Prompt</div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-stone-200 rounded-lg flex items-center justify-center">
                    {data.icon192 ? (
                      <img src={getImageUrl(data.icon192)} alt="App icon" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <svg className="w-6 h-6 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-stone-700 truncate">
                      {data.shortName || "App Name"}
                    </div>
                    <div className="text-xs text-stone-500">Install App</div>
                  </div>
                  <button
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      data.installPromptEnabled
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {data.installPromptEnabled ? "Available" : "Disabled"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-stone-100">
        <button
          onClick={() => onSave(data)}
          disabled={saving || readOnly}
          className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : readOnly ? "View Only" : "Save PWA Settings"}
        </button>
      </div>
    </div>
  );
}
